import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, hasPerm, fmtDate } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Wallet, Plus, Loader2, Trash2, Search, Pencil, SlidersHorizontal, Save, Tag, X, Upload, FileText, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Salary", "Electricity", "Water", "Transport", "Maintenance", "Stationery", "Internet", "Food", "Events", "Other"];
const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque", "Other"];

function EditableSelect({ label, value, onChange, baseOptions, testid, placeholder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [customs, setCustoms] = useState([]);
  const wrapRef = useRef(null);
  const options = Array.from(new Set([...baseOptions, ...customs, ...(value ? [value] : [])].filter(Boolean)));
  const query = q.trim();
  const filtered = query ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase())) : options;
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setQ(""); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const close = () => { setOpen(false); setQ(""); };
  const commit = (raw) => {
    const c = (raw || "").trim();
    if (!c) return;
    if (!options.includes(c)) setCustoms((p) => [...p, c]);
    onChange(c); close();
  };
  const pick = (o) => { onChange(o); close(); };
  return (
    <div ref={wrapRef} className="relative">
      <Label>{label}</Label>
      <div className="relative mt-1">
        {open && <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />}
        <input
          data-testid={testid}
          value={open ? q : (value || "")}
          placeholder={open ? "Type here to search" : (placeholder || "Select")}
          onFocus={() => { setOpen(true); setQ(""); }}
          onChange={(e) => { setOpen(true); setQ(e.target.value); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(q); } else if (e.key === "Escape" && open) { e.preventDefault(); e.stopPropagation(); close(); } }}
          className={`h-9 w-full rounded-md border border-input bg-transparent ${open ? "pl-8" : "pl-3"} pr-8 text-sm shadow-sm outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground`}
        />
        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-1 shadow-md max-h-60 overflow-y-auto" data-testid={`${testid}-list`}>
          <button type="button" disabled={!query} onMouseDown={(e) => e.preventDefault()} onClick={() => commit(query)} data-testid={`${testid}-add`} className="flex w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-sm font-medium text-indigo-600 hover:bg-accent disabled:opacity-40">
            <Plus size={14} />{query ? `Add "${query}"` : "Type to add new"}
          </button>
          {filtered.map((o) => (
            <button key={o} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(o)} data-testid={`${testid}-opt-${o}`} className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm text-slate-700 hover:bg-accent">
              <span className="truncate">{o}</span>{value === o && <Check size={14} className="text-indigo-600 shrink-0" />}
            </button>
          ))}
          {filtered.length === 0 && query && <div className="px-2 py-3 text-center text-sm text-muted-foreground">No matches</div>}
        </div>
      )}
    </div>
  );
}

export default function Expenses() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const blankExpense = { date: new Date().toISOString().slice(0, 10), category: "Salary", description: "", amount: 0, payment_mode: "Cash", paid_for: "", received_by: "", utr: "", account_no: "", voucher_no: "", paid_to: "", approved_by: "", paid_by: "", bill: "", bill_name: "", remarks: "" };
  const [f, setF] = useState(blankExpense);
  const openAdd = () => { setEditId(null); setF(blankExpense); setOpen(true); };
  const openEdit = (e) => { setEditId(e.id); setF({ date: e.date || "", category: e.category || "", description: e.description || "", amount: e.amount ?? 0, payment_mode: e.payment_mode || "Cash", paid_for: e.paid_for || "", received_by: e.received_by || "", utr: e.utr || "", account_no: e.account_no || "", voucher_no: e.voucher_no || "", paid_to: e.paid_to || "", approved_by: e.approved_by || "", paid_by: e.paid_by || "", bill: e.bill || "", bill_name: e.bill_name || "", remarks: e.remarks || "" }); setOpen(true); };
  const [search, setSearch] = useState("");
  const emptyCF = { date: "", date_from: "", date_to: "", category: "", paid_for: "", paid_to: "", payment_mode: "" };
  const [cf, setCf] = useState(emptyCF);
  const [showFilters, setShowFilters] = useState(false);
  const setCfKey = (k, v) => setCf((p) => ({ ...p, [k]: v }));

  const [cats, setCats] = useState(CATS);
  const [modes, setModes] = useState(MODES);
  const [catDraft, setCatDraft] = useState(CATS);
  const [modeDraft, setModeDraft] = useState(MODES);
  const [newCat, setNewCat] = useState("");
  const [newMode, setNewMode] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const canSettings = hasPerm(user, "add_expense");

  const load = () => api.get("/expenses").then((r) => setList(r.data));
  const loadSettings = () => api.get("/expense-settings").then((r) => {
    const c = r.data.categories || CATS, m = r.data.payment_modes || MODES;
    setCats(c); setModes(m); setCatDraft(c); setModeDraft(m);
  }).catch(() => {});
  useEffect(() => { load(); loadSettings(); api.get("/staff").then((r) => setStaff(r.data.filter((s) => s.role !== "admin"))).catch(() => {}); }, []);

  const addCat = () => { const c = newCat.trim(); if (c && !catDraft.includes(c)) setCatDraft([...catDraft, c]); setNewCat(""); };
  const removeCat = (c) => setCatDraft(catDraft.filter((x) => x !== c));
  const addMode = () => { const m = newMode.trim(); if (m && !modeDraft.includes(m)) setModeDraft([...modeDraft, m]); setNewMode(""); };
  const removeMode = (m) => setModeDraft(modeDraft.filter((x) => x !== m));
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await api.put("/expense-settings", { categories: catDraft, payment_modes: modeDraft });
      setCats(r.data.categories || []); setModes(r.data.payment_modes || []);
      setCatDraft(r.data.categories || []); setModeDraft(r.data.payment_modes || []);
      toast.success("Expense settings saved");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSavingSettings(false); }
  };

  const save = async () => {
    try {
      if (editId) { await api.put(`/expenses/${editId}`, { ...f, amount: Number(f.amount) }); toast.success("Expense updated"); }
      else { await api.post("/expenses", { ...f, amount: Number(f.amount) }); toast.success("Expense added"); }
      setOpen(false); setEditId(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); };

  const onBill = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Bill must be under 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setF((p) => ({ ...p, bill: reader.result, bill_name: file.name }));
    reader.readAsDataURL(file);
  };

  if (!list) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  const filtered = list.filter((e) => {
    if (search.trim()) {
      const q = search.toLowerCase();
      const hay = [e.date, e.category, e.description, e.paid_for, e.paid_to, e.payment_mode, String(e.amount)].join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (cf.date && e.date !== cf.date) return false;
    if (cf.date_from && (e.date || "") < cf.date_from) return false;
    if (cf.date_to && (e.date || "") > cf.date_to) return false;
    if (cf.category && e.category !== cf.category) return false;
    if (cf.paid_for && String(e.paid_for || "") !== cf.paid_for) return false;
    if (cf.paid_to && String(e.paid_to || "") !== cf.paid_to) return false;
    if (cf.payment_mode && e.payment_mode !== cf.payment_mode) return false;
    return true;
  });
  const total = filtered.reduce((a, e) => a + e.amount, 0);
  const catTotals = Object.entries(filtered.reduce((m, e) => { m[e.category] = (m[e.category] || 0) + e.amount; return m; }, {})).sort((a, b) => b[1] - a[1]);
  const activeFilters = Object.values(cf).filter(Boolean).length;
  const catOptions = Array.from(new Set([...cats, ...list.map((e) => e.category)].filter(Boolean)));
  const staffNames = staff.map((s) => s.name).filter(Boolean);
  const paidForOptions = Array.from(new Set([...staffNames, ...list.map((e) => e.paid_for)].filter(Boolean)));
  const receivedByOptions = Array.from(new Set([...staffNames, ...list.map((e) => e.received_by)].filter(Boolean)));
  const paidOptions = Array.from(new Set([...staffNames, ...list.map((e) => e.paid_to)].filter(Boolean)));
  const approvedByOptions = Array.from(new Set([...staffNames, ...list.map((e) => e.approved_by)].filter(Boolean)));
  const paidByOptions = Array.from(new Set([...staffNames, ...list.map((e) => e.paid_by)].filter(Boolean)));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-extrabold text-slate-900">Expenses</h1><p className="text-slate-500">{list.length} expenses recorded</p></div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex items-end gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5" data-testid="date-range-filter">
            <div><Label className="text-[11px] text-slate-500">From</Label><Input type="date" value={cf.date_from} onChange={(e) => setCfKey("date_from", e.target.value)} className="h-9" data-testid="filter-date-from" /></div>
            <div><Label className="text-[11px] text-slate-500">To</Label><Input type="date" value={cf.date_to} onChange={(e) => setCfKey("date_to", e.target.value)} className="h-9" data-testid="filter-date-to" /></div>
            {(cf.date_from || cf.date_to) && <Button variant="ghost" size="sm" className="text-slate-500" onClick={() => { setCfKey("date_from", ""); setCfKey("date_to", ""); }} data-testid="clear-date-range-btn">Clear</Button>}
          </div>
          {hasPerm(user, "add_expense") && <Button onClick={openAdd} data-testid="add-expense-btn"><Plus size={18} className="mr-2" />Add Expense</Button>}
        </div>
      </div>

      <Tabs defaultValue="expenses">
        <TabsList data-testid="expense-tabs">
          <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-expense-settings">Expense Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="expenses" className="space-y-6 mt-4">
      {filtered.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="expense-summary">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Category Breakdown</h3>
            <span className="text-xs text-slate-400">{catTotals.length} categories</span>
          </div>
          <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {catTotals.map(([cat, amt]) => {
              const pct = total > 0 ? Math.round((amt / total) * 100) : 0;
              return (
                <div key={cat} data-testid={`cat-summary-${cat}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-slate-700 truncate">{cat}</span>
                    <span className="tabular text-slate-600">{fmt(amt)} <span className="text-xs text-slate-400">({pct}%)</span></span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search date, category, description, paid to, mode…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="expense-search" />
          </div>
          <Button variant={showFilters ? "default" : "outline"} onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters-btn"><SlidersHorizontal size={15} className="mr-1.5" />Filters{activeFilters > 0 ? ` (${activeFilters})` : ""}</Button>
          {(search || activeFilters > 0) && <Button variant="ghost" size="sm" className="text-slate-500" onClick={() => { setCf(emptyCF); setSearch(""); }} data-testid="clear-filters-btn">Clear</Button>}
          <span className="text-xs text-slate-400">{filtered.length} of {list.length}</span>
          <div className="flex items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-5 py-2 ml-auto" data-testid="expenses-total-bar">
            <span className="text-sm font-medium text-slate-600">Total Expenses{(search || activeFilters > 0) ? " (filtered)" : ""}</span>
            <span className="text-xl font-heading font-extrabold text-indigo-700 tabular">{fmt(total)}</span>
          </div>
        </div>
        {showFilters && (
          <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
            <div><Label className="text-xs text-slate-500">Date</Label><Input type="date" value={cf.date} onChange={(e) => setCfKey("date", e.target.value)} data-testid="filter-date" /></div>
            <div><Label className="text-xs text-slate-500">Category</Label>
              <Select value={cf.category || "__all"} onValueChange={(v) => setCfKey("category", v === "__all" ? "" : v)}>
                <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__all">All</SelectItem>{catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-500">Paid For</Label>
              <Select value={cf.paid_for || "__all"} onValueChange={(v) => setCfKey("paid_for", v === "__all" ? "" : v)}>
                <SelectTrigger data-testid="filter-paidfor"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__all">All</SelectItem>{paidForOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-500">Paid To</Label>
              <Select value={cf.paid_to || "__all"} onValueChange={(v) => setCfKey("paid_to", v === "__all" ? "" : v)}>
                <SelectTrigger data-testid="filter-paidto"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__all">All</SelectItem>{paidOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-500">Mode</Label>
              <Select value={cf.payment_mode || "__all"} onValueChange={(v) => setCfKey("payment_mode", v === "__all" ? "" : v)}>
                <SelectTrigger data-testid="filter-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__all">All</SelectItem>{modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 table-scroll">
        {filtered.length === 0 ? <div className="py-16 text-center text-slate-400"><Wallet size={40} className="mx-auto mb-2 opacity-40" />{list.length === 0 ? "No expenses yet." : "No expenses match your search/filters."}</div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Paid For</th><th className="text-left px-4 py-3">Paid To</th><th className="text-left px-4 py-3">Approved By</th><th className="text-left px-4 py-3">Paid By</th><th className="text-right px-4 py-3">Amount</th><th className="text-left px-4 py-3">Mode</th><th className="text-left px-4 py-3">Description</th><th className="px-4 py-3"></th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
                  <td className="px-4 py-3">{fmtDate(e.date)}</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs">{e.category}</span>{e.source === "payroll" && <span className="ml-1.5 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold" data-testid={`payroll-tag-${e.id}`}>Payroll</span>}</td>
                  <td className="px-4 py-3 text-slate-500">{e.paid_for}</td>
                  <td className="px-4 py-3 text-slate-500">{e.paid_to}</td>
                  <td className="px-4 py-3 text-slate-500">{e.approved_by}</td>
                  <td className="px-4 py-3 text-slate-500">{e.paid_by}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular text-rose-600">{fmt(e.amount)}</td>
                  <td className="px-4 py-3">{e.payment_mode}</td>
                  <td className="px-4 py-3 text-slate-600">{e.description}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {e.source === "payroll" ? <span className="text-xs text-slate-400">Auto</span> : <>
                      {hasPerm(user, "add_expense") && <Button size="icon" variant="ghost" className="text-indigo-600" onClick={() => openEdit(e)} data-testid={`edit-expense-${e.id}`}><Pencil size={14} /></Button>}
                      {hasPerm(user, "delete") && <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => del(e.id)} data-testid={`delete-expense-${e.id}`}><Trash2 size={14} /></Button>}
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          {!canSettings ? (
            <div className="bg-white rounded-xl border border-slate-200 py-16 text-center text-slate-400">You don't have permission to manage expense settings.</div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="expense-cat-settings">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3"><Tag size={18} className="text-indigo-500" />Expense Categories</h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  {catDraft.length === 0 && <span className="text-sm text-slate-400">No categories yet.</span>}
                  {catDraft.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full pl-3 pr-1 py-1 text-sm" data-testid={`cat-chip-${c}`}>
                      {c}
                      <button onClick={() => removeCat(c)} className="p-0.5 rounded-full hover:bg-slate-200 text-slate-400 hover:text-rose-500" data-testid={`remove-cat-${c}`}><X size={14} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCat(); } }} placeholder="New category" data-testid="new-cat-input" />
                  <Button variant="outline" onClick={addCat} data-testid="add-cat-btn"><Plus size={15} /></Button>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="expense-mode-settings">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3"><Wallet size={18} className="text-emerald-500" />Payment Modes</h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  {modeDraft.length === 0 && <span className="text-sm text-slate-400">No payment modes yet.</span>}
                  {modeDraft.map((m) => (
                    <span key={m} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full pl-3 pr-1 py-1 text-sm" data-testid={`mode-chip-${m}`}>
                      {m}
                      <button onClick={() => removeMode(m)} className="p-0.5 rounded-full hover:bg-slate-200 text-slate-400 hover:text-rose-500" data-testid={`remove-mode-${m}`}><X size={14} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={newMode} onChange={(e) => setNewMode(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMode(); } }} placeholder="New payment mode" data-testid="new-mode-input" />
                  <Button variant="outline" onClick={addMode} data-testid="add-mode-btn"><Plus size={15} /></Button>
                </div>
              </div>

              <div className="lg:col-span-2 flex justify-end">
                <Button onClick={saveSettings} disabled={savingSettings} className="gap-1.5" data-testid="save-expense-settings-btn">{savingSettings ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save Settings</Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
      <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditId(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-none sm:w-[50vw] overflow-y-auto" data-testid="expense-drawer">
          <SheetHeader><SheetTitle>{editId ? "Edit Expense" : "Add Expense"}</SheetTitle></SheetHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 mt-4">
            <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="expense-date" className="mt-1" /></div>
            <EditableSelect label="Category" value={f.category} onChange={(v) => setF({ ...f, category: v })} baseOptions={catOptions} testid="expense-category" />
            <div><Label>Amount</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="expense-amount" className="mt-1" /></div>
            <div><Label>Payment Mode</Label><Select value={f.payment_mode} onValueChange={(v) => setF({ ...f, payment_mode: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
            <EditableSelect label="Paid For" value={f.paid_for} onChange={(v) => setF({ ...f, paid_for: v })} baseOptions={paidForOptions} testid="expense-paidfor" placeholder="Select or add" />
            <EditableSelect label="Received By" value={f.received_by} onChange={(v) => setF({ ...f, received_by: v })} baseOptions={receivedByOptions} testid="expense-receivedby" placeholder="Select or add" />
            <EditableSelect label="Paid To" value={f.paid_to} onChange={(v) => setF({ ...f, paid_to: v })} baseOptions={paidOptions} testid="expense-paidto" placeholder="Select or add" />
            <EditableSelect label="Approved By" value={f.approved_by} onChange={(v) => setF({ ...f, approved_by: v })} baseOptions={approvedByOptions} testid="expense-approvedby" placeholder="Select or add" />
            <EditableSelect label="Paid By" value={f.paid_by} onChange={(v) => setF({ ...f, paid_by: v })} baseOptions={paidByOptions} testid="expense-paidby" placeholder="Select or add" />
            <div><Label>UTR / Reference No.</Label><Input value={f.utr} onChange={(e) => setF({ ...f, utr: e.target.value })} placeholder="e.g. UTR123456" data-testid="expense-utr" className="mt-1" /></div>
            <div><Label>Account No.</Label><Input value={f.account_no} onChange={(e) => setF({ ...f, account_no: e.target.value })} placeholder="Bank account no." data-testid="expense-accountno" className="mt-1" /></div>
            <div><Label>Voucher No.</Label><Input value={f.voucher_no} onChange={(e) => setF({ ...f, voucher_no: e.target.value })} placeholder="Voucher no." data-testid="expense-voucherno" className="mt-1" /></div>
            <div className="sm:col-span-2">
              <Label>Upload Bill</Label>
              <div className="mt-1 flex items-center gap-2">
                <label className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50 text-sm text-slate-600" data-testid="expense-bill-label">
                  <Upload size={15} /><span>Choose file</span>
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onBill} data-testid="expense-bill-input" />
                </label>
                {f.bill && <a href={f.bill} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-indigo-600 truncate max-w-[160px]" data-testid="expense-bill-link"><FileText size={14} />{f.bill_name || "View bill"}</a>}
                {f.bill && <button onClick={() => setF({ ...f, bill: "", bill_name: "" })} className="text-slate-400 hover:text-rose-500" data-testid="expense-bill-remove"><X size={15} /></button>}
              </div>
            </div>
            <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Add a note…" data-testid="expense-desc" className="mt-1" /></div>
          </div>
          <SheetFooter className="mt-5">
            <Button variant="outline" onClick={() => { setOpen(false); setEditId(null); }}>Cancel</Button>
            <Button onClick={save} data-testid="save-expense-btn">Save</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
